import { useCallback, useEffect, useState } from "react";
import type { AuditEventDto } from "@grizzly/shared";
import type { ApiClientError } from "../lib/apiClient.js";
import { api } from "../lib/apiClient.js";
import { formatDateTime } from "../lib/format.js";
import { Badge } from "../components/ui/badge.js";
import { PageHeader } from "../components/ui/card.js";
import { DataTable } from "../components/ui/dataTable.js";
import { SelectInput } from "../components/ui/field.js";
import { useI18n, type MessageKey } from "../i18n/index.js";

const ACTION_LABEL_KEYS: Record<string, MessageKey> = {
  "auth.login": "audit.action.signIn",
  "auth.login_failed": "audit.action.signInFailed",
  "auth.logout": "audit.action.signOut",
  "user.create": "audit.action.userCreated",
  "user.role_change": "audit.action.roleChanged",
  "user.activate": "audit.action.userActivated",
  "user.deactivate": "audit.action.userDeactivated",
  "attachment.upload": "audit.action.documentUploaded",
  "attachment.download": "audit.action.documentDownloaded",
  "attachment.delete": "audit.action.documentDeleted",
};

function actionTone(action: string): "neutral" | "active" | "risk" | "complete" {
  if (action.endsWith("_failed")) return "risk";
  if (action.startsWith("auth.")) return "neutral";
  if (action.startsWith("attachment.")) return "active";
  return "complete";
}

export function AuditTrailPage() {
  const { t } = useI18n();
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(
    null
  );
  const [items, setItems] = useState<AuditEventDto[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listAuditEvents(action || undefined);
      setItems(res.items);
    } catch (err) {
      const e = err as ApiClientError;
      setError({ message: e.message, requestId: e.requestId });
    } finally {
      setLoading(false);
    }
  }, [action]);

  useEffect(() => {
    void load();
  }, [load]);

  const actionOptions: { value: string; label: string }[] = [
    { value: "", label: t("audit.filter.allActions") },
    { value: "auth.login", label: t("audit.action.signIn") },
    { value: "auth.login_failed", label: t("audit.action.signInFailed") },
    { value: "auth.logout", label: t("audit.action.signOut") },
    { value: "user.create", label: t("audit.action.userCreated") },
    { value: "user.role_change", label: t("audit.action.roleChanged") },
    { value: "user.activate", label: t("audit.action.userActivated") },
    { value: "user.deactivate", label: t("audit.action.userDeactivated") },
    { value: "attachment.upload", label: t("audit.action.documentUploaded") },
    { value: "attachment.download", label: t("audit.action.documentDownloaded") },
    { value: "attachment.delete", label: t("audit.action.documentDeleted") },
  ];

  const actionLabel = (rawAction: string): string => {
    const key = ACTION_LABEL_KEYS[rawAction];
    return key ? t(key) : rawAction;
  };

  return (
    <>
      <PageHeader title={t("nav.auditTrail")} description={t("audit.description")} />
      <div className="mb-4 max-w-xs">
        <SelectInput
          label={t("audit.filterByAction")}
          options={actionOptions}
          value={action}
          onChange={(e) => setAction(e.target.value)}
        />
      </div>
      <DataTable
        caption={t("audit.caption")}
        loading={loading}
        error={error}
        onRetry={load}
        rows={items}
        rowKey={(row) => row.id}
        emptyTitle={t("audit.emptyTitle")}
        emptyDescription={t("audit.emptyDescription")}
        columns={[
          {
            key: "when",
            header: t("common.when"),
            render: (row) => (
              <span className="tabular whitespace-nowrap">
                {formatDateTime(row.occurredAt)}
              </span>
            ),
          },
          {
            key: "action",
            header: t("audit.column.action"),
            render: (row) => (
              <Badge tone={actionTone(row.action)}>{actionLabel(row.action)}</Badge>
            ),
          },
          {
            key: "entity",
            header: t("audit.column.record"),
            render: (row) => (
              <span className="text-xs">
                {row.entityType}
                <span className="tabular block text-ink-muted">{row.entityId}</span>
              </span>
            ),
          },
          {
            key: "actor",
            header: t("audit.column.actor"),
            render: (row) => row.actorEmail ?? t("audit.system"),
          },
          {
            key: "request",
            header: t("audit.column.requestId"),
            render: (row) => (
              <span className="tabular break-all text-xs text-ink-muted">
                {row.requestId ?? "-"}
              </span>
            ),
          },
          {
            key: "reason",
            header: t("common.fields.reason"),
            render: (row) => row.reason ?? "-",
          },
        ]}
      />
    </>
  );
}
